import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';

/**
 * Multi-pack picker (mugs, can coolers). Selecting a pack sets the product form's quantity;
 * changing the quantity selector re-selects the matching pack (or none).
 */
class MbPackPicker extends HTMLElement {
  #abort = new AbortController();

  connectedCallback() {
    this.addEventListener('change', this.#onPackChange);
    const { signal } = this.#abort;
    const sync = (/** @type {Event} */ event) => {
      const target = /** @type {Element | null} */ (event.target instanceof Element ? event.target : null);
      if (target?.closest('quantity-selector-component') && this.#form?.contains(target)) {
        setTimeout(() => this.#syncFromQuantity(), 0);
      }
    };
    document.addEventListener('click', sync, { signal });
    document.addEventListener('change', sync, { signal });
    document.addEventListener('input', sync, { signal });
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onPackChange);
    this.#abort.abort();
    this.#abort = new AbortController();
  }

  get #form() {
    return document.querySelector(`product-form-component[data-section-id="${this.dataset.sectionId}"]`);
  }

  get #radios() {
    return /** @type {HTMLInputElement[]} */ (Array.from(this.querySelectorAll('input[type="radio"]')));
  }

  /** @param {Event} event */
  #onPackChange = (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement) || input.type !== 'radio') return;
    this.#markChecked(input);
    this.#setQuantity(Number(input.value) || 1);
  };

  /** @param {HTMLInputElement | null} checked */
  #markChecked(checked) {
    for (const radio of this.#radios) {
      radio.dataset.previousChecked = radio.dataset.currentChecked === 'true' ? 'true' : 'false';
      radio.dataset.currentChecked = radio === checked ? 'true' : 'false';
      if (radio !== checked) radio.checked = false;
    }
    if (checked) checked.checked = true;
  }

  /** @param {number} quantity */
  #setQuantity(quantity) {
    const form = /** @type {HTMLElement | null} */ (this.#form);
    if (!form) return;
    const selector = /** @type {any} */ (form.querySelector('quantity-selector-component'));
    const input = /** @type {HTMLInputElement | null} */ (form.querySelector('input[name="quantity"]'));
    if (selector?.setValue) selector.setValue(String(quantity));
    else if (input) input.value = String(quantity);
    if (input) input.dispatchEvent(new Event('change', { bubbles: true }));
    // Fallback used by product-form when there is no quantity selector at all
    form.dataset.quantityDefault = String(quantity);
  }

  #syncFromQuantity() {
    const input = /** @type {HTMLInputElement | null} */ (this.#form?.querySelector('input[name="quantity"]'));
    if (!input) return;
    const match = this.#radios.find((radio) => radio.value === String(Number(input.value)));
    if (match && match.checked) return;
    this.#markChecked(match ?? null);
  }
}

/**
 * Compact quick add used by the cart "ships in the same box" row. Adds the selected variant
 * and refreshes every cart section through the theme's standard cart event.
 */
class MbQuickAdd extends HTMLElement {
  connectedCallback() {
    this.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.removeEventListener('submit', this.#onSubmit);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    const form = /** @type {HTMLFormElement} */ (event.target);
    const button = /** @type {HTMLButtonElement | null} */ (form.querySelector('button[type="submit"]'));
    const id = String(new FormData(form).get('id') || '');
    if (!id || button?.disabled) return;
    if (button) {
      button.disabled = true;
      button.setAttribute('aria-busy', 'true');
    }

    const sectionIds = Array.from(document.querySelectorAll('cart-items-component'))
      .map((el) => (el instanceof HTMLElement ? el.dataset.sectionId : ''))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'cart',
        lines: [{ merchandiseId: id, quantity: 1 }],
        promise: deferred.promise,
      })
    );

    const routes = /** @type {any} */ (window).Theme?.routes ?? {};
    const addUrl = routes.cart_add_url || '/cart/add.js';
    const cartUrl = routes.cart_url || '/cart';

    try {
      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items: [{ id: Number(id), quantity: 1 }],
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const data = await response.json();
      const cart = await fetch(`${cartUrl}.js`, { headers: { Accept: 'application/json' } }).then((r) => r.json());

      if (data.status) {
        this.dispatchEvent(
          new CartErrorEvent({ error: data.description || data.message || 'Add to cart failed', code: 'INVALID' })
        );
      }

      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'mb-quick-add',
          sections: data.sections,
          didError: Boolean(data.status),
        },
      });
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: 'Network error during add to cart', code: 'SERVICE_UNAVAILABLE' }));
    } finally {
      if (button && button.isConnected) {
        button.disabled = false;
        button.removeAttribute('aria-busy');
      }
    }
  };
}

if (!customElements.get('mb-pack-picker')) customElements.define('mb-pack-picker', MbPackPicker);
if (!customElements.get('mb-quick-add')) customElements.define('mb-quick-add', MbQuickAdd);
